import React, { useMemo, useState } from "react";

import {
  HouseholdAccount,
  HouseholdAccountTypePagination,
} from "@fartherfinance/frontend/api/Households/types";
import { PaginationMetadata } from "@fartherfinance/frontend/api/Types";

import { getAccountProviderDisplayName } from "../../helpers";
import {
  AccountMenuActions,
  householdAccountsColumns,
} from "@src/multiCustodian/components/Advisor/Households/constants";
import Chip from "@src/sharedComponents/Chip/Chip";
import PaginatedTable from "@src/sharedComponents/Table/PaginatedTable";
import Text from "@src/sharedComponents/Typography/Text";

import HouseholdAccountsModalManager from "./HouseholdAccountsModalManager";

import styles from "./HouseholdAccountsTable.module.css";

interface HouseholdAccountsTableProps {
  accounts: HouseholdAccount[] | undefined;
  pagination: PaginationMetadata | undefined;
  isLoading?: boolean;
  currentPage?: number;
  handleOnPageChange?: (
    page: number,
    accountType: HouseholdAccountTypePagination
  ) => void;
  accountType: HouseholdAccountTypePagination;
}

const HouseholdAccountsTable: React.FC<HouseholdAccountsTableProps> = ({
  accounts,
  pagination,
  isLoading = false,
  currentPage = 1,
  handleOnPageChange,
  accountType,
}) => {
  const [_currentView, _setCurrentView] = useState<AccountMenuActions | null>(
    null
  );
  const [selectedAccount, setSelectedAccount] =
    useState<HouseholdAccount | null>(null);

  const _handleActionSelected = (
    action: AccountMenuActions,
    account: HouseholdAccount
  ) => {
    _setCurrentView(action);
    setSelectedAccount(account);
  };

  const closeModal = () => {
    setSelectedAccount(null);
  };
  const tableRowData = useMemo(() => {
    return (
      accounts?.map((account) => ({
        key: account.accountId,
        accountName: {
          value: (
            <Text variant="body" className={styles.accountValue}>
              {account.name}
            </Text>
          ),
        },
        number: {
          value: (
            <Text variant="body" className={styles.accountNumber}>
              {account.number}
            </Text>
          ),
        },
        owner: {
          value: (
            <Text variant="body">
              {account.member.firstName} {account.member.lastName}
            </Text>
          ),
        },
        type: {
          value: <Chip label={account.type} variant="outlined" size="small" />,
        },
        custodian: {
          value: <Text variant="body">{account.custodian}</Text>,
        },
        modelPortfolio: {
          value: (
            <Text variant="body" className={styles.modelPortfolio}>
              {account.portfolio !== null
                ? account.portfolio.type === "Portfolio-Plan"
                  ? account.portfolio.portfolioPlan.name
                  : account.portfolio.portfolioFamily.name
                : "--"}
            </Text>
          ),
        },
        amount: {
          value: (
            <Text variant="body" className={styles.amount}>
              ${account.amount.toFixed(2)}
            </Text>
          ),
        },
      })) || []
    );
  }, [accounts]);

  const columns = useMemo(() => {
    return householdAccountsColumns.map((column) => {
      if (
        column.accessor === "accountName" &&
        accounts &&
        accounts.length > 0
      ) {
        return {
          ...column,
          label: getAccountProviderDisplayName(accounts[0]),
        };
      }
      return column;
    });
  }, [accounts]);

  const tablePagination = useMemo(() => {
    if (pagination === undefined) {
      return {
        currentPage: currentPage,
        totalPages: 1,
        pageSize: 10,
        totalCount: 0,
        prevPage: undefined,
        nextPage: undefined,
      };
    }

    const totalPages = Math.ceil(pagination.totalCount / pagination.pageSize);

    return {
      currentPage: currentPage,
      totalPages,
      pageSize: pagination.pageSize,
      totalCount: pagination.totalCount,
      prevPage: currentPage === 1 ? undefined : currentPage - 1,
      nextPage: currentPage === totalPages ? undefined : currentPage + 1,
    };
  }, [pagination, currentPage]);

  return (
    <>
      <PaginatedTable
        isLoading={isLoading}
        columns={columns}
        rows={tableRowData}
        rowClassName={styles.tableRow}
        pagination={tablePagination}
        showEmptyRows={false}
        onPageChange={(page: number) => handleOnPageChange?.(page, accountType)}
      />
      <HouseholdAccountsModalManager
        currentView={_currentView}
        account={selectedAccount}
        onClose={closeModal}
      />
    </>
  );
};

export default HouseholdAccountsTable;
